import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function InboundCalls() {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'ri-phone-line',
      title: 'Call Tracking Systems',
      description: 'Advanced call tracking technology to monitor and attribute calls to specific marketing campaigns.'
    },
    {
      icon: 'ri-route-line',
      title: 'Intelligent Call Routing',
      description: 'Smart routing algorithms that direct calls to the most qualified sales representatives.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Lead Qualification Scripts',
      description: 'Proven qualification scripts designed to maximize conversion rates and identify serious prospects.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Performance Analytics',
      description: 'Comprehensive analytics dashboard tracking call volume, duration, conversion rates, and ROI.'
    }
  ];

  const benefits = [
    {
      title: '300% More Calls',
      description: 'Dramatically increase inbound call volume through optimized campaigns.',
      icon: 'ri-phone-fill'
    },
    {
      title: '45% Higher Conversion',
      description: 'Improve call-to-customer conversion rates with better qualification processes.',
      icon: 'ri-arrow-up-circle-line'
    },
    {
      title: 'Real-time Attribution',
      description: 'Know exactly which marketing efforts are driving your highest-value calls.',
      icon: 'ri-eye-line'
    },
    {
      title: '24/7 Monitoring',
      description: 'Round-the-clock call monitoring and optimization for maximum performance.',
      icon: 'ri-24-hours-line'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Call System Setup',
      description: 'Implement advanced call tracking and routing systems integrated with your existing infrastructure.'
    },
    {
      step: '02',
      title: 'Campaign Optimization',
      description: 'Optimize all marketing campaigns to drive high-quality inbound calls from qualified prospects.'
    },
    {
      step: '03',
      title: 'Script Development',
      description: 'Create and refine call qualification scripts to maximize conversion and lead quality.'
    },
    {
      step: '04',
      title: 'Performance Analysis',
      description: 'Continuous monitoring and optimization based on call analytics and conversion data.'
    }
  ];

  const stats = [
    { number: '2.8M+', label: 'Inbound Calls Generated' },
    { number: '28%', label: 'Average Conversion Rate' },
    { number: '4.2x', label: 'ROI Improvement' },
    { number: '85%', label: 'Call Answer Rate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.8), rgba(22, 163, 74, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20call%20center%20team%20managing%20inbound%20leads%20with%20headsets%20and%20multiple%20computer%20monitors%20showing%20call%20analytics%20and%20customer%20management%20systems%20in%20modern%20office%20environment%20with%20active%20phone%20conversations&width=1920&height=800&seq=inbound-calls-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Inbound Call Management
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8">
              Optimize inbound call lead capture and qualification processes with advanced call tracking, routing systems, and conversion optimization to maximize ROI from all marketing channels.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100"
              >
                <i className="ri-phone-line mr-2 text-black"></i>
                Optimize Calls Now
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-green-600"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Results
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Inbound Call Performance Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our call management solutions deliver measurable improvements in call volume and conversion rates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Transform Your Inbound Call Strategy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Turn every inbound call into a qualified opportunity with our advanced call management solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-2xl text-green-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Advanced Call Management Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools and systems designed to maximize the value of every inbound call.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20call%20center%20dashboard%20showing%20real-time%20call%20analytics%2C%20routing%20systems%2C%20and%20performance%20metrics%20on%20multiple%20screens%2C%20professional%20telecommunications%20technology%20setup%20with%20headsets%20and%20monitoring%20equipment&width=600&height=400&seq=call-management&orientation=landscape"
                alt="Call Management Dashboard"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div>
              <div className="space-y-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mr-4 flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-green-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Call Optimization Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach to maximizing the value and conversion potential of your inbound calls.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20telecommunications%20technology%20showing%20call%20routing%20algorithms%2C%20phone%20system%20integrations%2C%20and%20analytics%20dashboards%20with%20real-time%20call%20data%20visualization%2C%20modern%20business%20communication%20infrastructure&width=600&height=400&seq=call-technology&orientation=landscape"
                alt="Call Technology"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Enterprise-Grade Call Infrastructure
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our advanced call management platform integrates seamlessly with your existing systems to provide unmatched visibility and control over your inbound call operations.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-gray-700">Dynamic number insertion and call tracking</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-gray-700">AI-powered call scoring and qualification</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-gray-700">Real-time call recording and analysis</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <span className="text-gray-700">CRM integration and automated lead routing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Maximize Your Inbound Calls?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Let our call management experts help you turn more inbound calls into qualified leads and paying customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-green-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
